import {Component, OnInit, inject, Renderer2} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {RouterModule} from '@angular/router';
import {TranslocoModule} from '@ngneat/transloco';
import {AsyncPipe, NgIf, DOCUMENT} from "@angular/common";
import LoaderComponent from "../../components/loader/loader.component";
import {NavigationService} from "../../services/navigation.service";
import {ProductCardComponent} from '../../components/product-card/product-card.component';
import {NgxIntlTelInputModule} from "ngx-intl-tel-input-gg";
import {SearchProductFiltersComponent} from "./components/search-product-filters/search-product-filters.component";
import {trigger, transition, style, animate} from '@angular/animations';
import {Store} from "@ngrx/store";
import {AppState} from "../../store/app.state";
import {
    selectMarketplaceFilteredProducts,
    selectMarketplaceIsFilteredProductsEmpty,
    selectMarketplaceIsLoading,
    selectMarketplaceShowFilters,
    selectMarketplaceSort
} from "../../store/marketplace/marketplace.selectors";
import {
    fetchProducts,
    setMarketplaceSort,
    toggleMarketplaceShowFilters
} from "../../store/marketplace/marketplace.actions";
import {MarketplaceFilteredProducts, SearchProductSortType} from "../../store/marketplace/marketplace.state";
import {first, map} from "rxjs";
import {Product} from "../../models/product.model";
import {MarketplaceFooterComponent} from "./components/marketplace-footer/marketplace-footer.component";

interface MarketplaceSection {
    key: string;
    title: string;
    description?: string;
    descriptionKey?: string;
    products: Product[];
}

@Component({
    selector: 'marketplace-page',
    standalone: true,
    imports: [FormsModule, RouterModule, ProductCardComponent, TranslocoModule, NgIf, AsyncPipe, LoaderComponent, NgxIntlTelInputModule, ReactiveFormsModule, SearchProductFiltersComponent, MarketplaceFooterComponent],
    templateUrl: './marketplace-page.component.html',
    styleUrl: './marketplace-page.component.scss',
    animations: [
        trigger('slideInOut', [
            transition(':enter', [
                style({transform: 'translateX(-100%)'}), // entra da sinistra
                animate('300ms ease-out', style({transform: 'translateX(0)'}))
            ]),
            transition(':leave', [
                animate('300ms ease-in', style({transform: 'translateX(-100%)'}))
            ])
        ])
    ],
})
export default class MarketplacePageComponent implements OnInit {
    #store = inject(Store<AppState>);
    private document = inject(DOCUMENT);
    private renderer = inject(Renderer2);

    filteredProducts$ = this.#store.select(selectMarketplaceFilteredProducts);
    marketplaceSections$ = this.filteredProducts$.pipe(
        map(filteredProducts => this.buildMarketplaceSections(filteredProducts))
    );
    isFilteredProductsEmpty$ = this.#store.select(selectMarketplaceIsFilteredProductsEmpty);
    isLoading$ = this.#store.select(selectMarketplaceIsLoading);
    showFilters$ = this.#store.select(selectMarketplaceShowFilters);

    sortType: SearchProductSortType | null = null;

    constructor(
        private navigationService: NavigationService
    ) {
        this.navigationService.saveCurrentRoute(true);
        this.#store.select(selectMarketplaceSort).pipe(first()).subscribe(value => {
            this.sortType = value ?? null;
        });
    }

    ngOnInit(): void {
        this.#store.dispatch(fetchProducts());
    }

    toggleFilters() {
        this.#store.dispatch(toggleMarketplaceShowFilters())
        const body = this.document.body;
        this.renderer.addClass(body, 'overflow-hidden');
    }

    sortTypeChange() {
        this.#store.dispatch(setMarketplaceSort({sort: this.sortType}));
    }

    navigateAndReload() {
        window.location.reload();
    }

    private buildMarketplaceSections(filteredProducts: MarketplaceFilteredProducts): MarketplaceSection[] {
        const plusProducts = (filteredProducts.categories['Plus']?.length ?? 0) > 0
            ? filteredProducts.categories['Plus']
            : filteredProducts.bundle;

        return [
            {
                key: 'Monitoring',
                title: 'Monitoring',
                descriptionKey: 'pages.marketplace-page.categoryLabels.Monitoring',
                products: filteredProducts.categories['Monitoring'] ?? [],
            },
            {
                key: 'Coaching',
                title: 'Coaching',
                descriptionKey: 'pages.marketplace-page.categoryLabels.Coaching',
                products: filteredProducts.categories['Coaching'] ?? [],
            },
            {
                key: 'Fitness',
                title: 'Fitness',
                descriptionKey: 'pages.marketplace-page.categoryLabels.Fitness',
                products: filteredProducts.categories['Fitness'] ?? [],
            },
            {
                key: 'Nutrizione',
                title: 'Nutrizione',
                descriptionKey: 'pages.marketplace-page.categoryLabels.Nutrizione',
                products: filteredProducts.categories['Nutrizione'] ?? [],
            },
            {
                key: 'Psicologia',
                title: 'Psicologia',
                descriptionKey: 'pages.marketplace-page.categoryLabels.Psicologia',
                products: filteredProducts.categories['Psicologia'] ?? [],
            },
            {
                key: 'Abbonamenti',
                title: 'Abbonamenti',
                description: 'Scegli il tuo benessere con semplicità',
                products: filteredProducts.subscription,
            },
            {
                key: 'Plus',
                title: 'Plus',
                description: '',
                products: plusProducts,
            },
        ].filter(section => section.products.length > 0);
    }
}
